import type {
  DownloadPdfPreferences,
  FormType,
  ModuleTemplate,
  PeriodicReviewChecklistItem,
  PeriodicReviewConfig,
  Translations,
  VersionState,
} from '@pbk-web/types';
import Cache from 'timed-cache';

import type { ActionTypeNames } from '../components/form/ActionTypes';
import type { FormBuilderPlaceholder, PlaceholderType, StepTrigger } from '../components/form-builder/FormBuilderTypes';
import type { Access } from './Access';
import type { TemplateAssociation } from './Association';
import type { PeriodicReviewConfigForGets } from './ClientForm';
import type { FormDefaultConfiguration } from './ClientFormDefaults';
import type { ClientFormSectionStatus } from './ClientFormSectionStatus';
/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ClientFormShare } from './ClientFormShare';
import type { ClientFormStatus } from './ClientFormStatus';
import type { ClientFormUserDto } from './ClientFormUser';
import { CUIDDateTimeOptions } from './CustomIdentifier';
import type { DistributionResponse } from './Distribution';

export type FormListItem = {
  id: string;
  templateId: string;
  code: string;
  version: number;
  subtitle: string;
  progress: number;
  overallProgress: number;
  status: ClientFormStatus;
  statusUtc: string;
  dueDateUtc: string | null;
  archivedUtc: string | null;
  startDateUtc: string | null;
  clientModuleId: string;
  clientModuleSectionId: string;
  templateModuleSectionId: string;
  isAsset?: boolean;
  translations: Translations<'title' | 'description'>;
  type: (typeof FormType)[keyof typeof FormType];
  users: ClientFormUserDto[];
  majorVersion: number;
  minorVersion: number;
  clientId: string;
  clientName: string;
  isShared: boolean;
  shares: ClientFormShare[];
  isPublic: boolean;
  unlockedSteps: string[];
  riskScore: number;
  totalRiskScore: number;
  createdUtc: string;
  accessType: Access;
  requiresApproval: boolean;
  requiresValidation: boolean;
  templateModuleName: string;
  templateModuleTranslations: ModuleTemplate['translations'];
  referenceCount: number | null;
  attachmentCount: number | null;
  templateReferenceCount: number | null;
  isSystemTemplateForm: boolean;
  effectiveDateUtc: string | null;
  modifiedUtc: string;
  distribution?: DistributionResponse;
  originId: string | null;
  baseId: string;
  customIdentifier: string;
  templateReferences: TemplateAssociation[] | null;
  trainingRequirementsUnmet: boolean;
  versionState?: VersionState;

  materialisedTargetSubtitle?: string; // only on form references response
  targetMajorVersion?: number; // only on form references response
} & PeriodicReviewConfigForGets;

export type FormConfig = {
  id: string;
  createdUtc: string;
  modifiedUtc: string;
  deletedUtc: string;
  archivedUtc: string | null;
  version: number;
  isLatest: boolean;
  code: string;
  category?: string;
  sections: FormSection[];
  iconCode: string;
  translations: Translations<'title' | 'publicTitle' | 'publicDescription' | 'predefinedTitle'>;
  isAssociated: boolean;
  associatedSectionTranslations: Translations<'associatedSectionName'>;
  type: (typeof FormType)[keyof typeof FormType];
  requiresApproval?: boolean;
  requiresApprovalSignature?: boolean;
  requiresValidation?: boolean;
  requiresValidationSignature?: boolean;
  requiresOwnerAcknowledgement?: boolean;
  answers: Record<string, any>;
  adHocAnswers: Record<string, AdHocAnswerResponse[]>;
  answerMetaData: Record<string, AnswerMetaData>;
  placeholders?: FormBuilderPlaceholder[];
  clientId?: string;
  isOwnedByClient?: boolean;
  periodicReviewConfig?: PeriodicReviewConfig;
  downloadPdfPreferences?: DownloadPdfPreferences;
  isSystem?: boolean;
  templateModuleId?: string;
  templateModuleSectionId?: string;
  quizConfig?: QuizConfig;
};

export type AnswerMetaData = {
  createdUtc: string;
  createdById: string;
};

export type QuizConfig = {
  allowedAttempts: number | null;
  showAnswersOnComplete: boolean;
  passRatePercentage: number;
};

export type PeriodicReviewChecklistItemInstance = PeriodicReviewChecklistItem & {
  checked: boolean;
};

export const PlaceholderFormats = ['comma', 'bullet', 'number', 'plain', 'link', ...CUIDDateTimeOptions] as const;

export type PlaceholderFormat = (typeof PlaceholderFormats)[number] | null;

export type ActionPlaceholderData = {
  actionType: ActionTypeNames;
  // translations: Translations; // Not populated currently
  friendlyName: string;
  dataFormat: PlaceholderFormat;
  answers: any[] | null;
  clientFormIds: string[];
  actionIds: string[];
  type: PlaceholderType;
  fallbackValue?: string;
};

export type FormSection = {
  id: string;
  actions: FormAction[];
  triggerSteps: StepTrigger[];
  triggersNextStep: boolean;
  visible?: string;
  isDraft?: boolean;
  isPublic?: boolean;
  requiresApproval?: boolean;
  translations: Translations<'title'>;
  number?: string; //used for display purposes
  isHiddenFromExport?: boolean;
};

/**
 * Holds info about the next steps to be unlocked
 */
export type StepInfoResponse = {
  id: string;
  title: string;
  translations: Translations;
};

export type FormAction = {
  id: string;
  visible: string;
  type: ActionTypeNames;
  data?: Record<string, any>;
  required?: string;
  noninteractive?: boolean;
  translations?: Translations;
  previewHidden?: boolean;
  riskEnabled?: boolean;
  riskConditions?: string[];
  riskNoMatch?: string;
  shownInOverview?: boolean;
  shownInResourceOverview?: boolean;
  number?: string; //used for display purposes
};

export type FavouriteForm = {
  id?: string;
  templateFormId: string;
  sortOrder: number;
  color: string;
  translations: Translations<'title'>;
  configuration?: FormDefaultConfiguration;
};

export type ActionHighlightInfo = {
  formId?: string;
  sectionId?: string;
  sourceId?: string;
  isRisk?: boolean;
};

export type GetAllTemplatesRequest = {
  latestOnly?: boolean;
  includeArchived?: boolean;
  includeActive?: boolean;
  types?: (typeof FormType)[keyof typeof FormType][];
  searchTerm?: string;
  clientModuleId?: string;
  templateIds?: string[];
};

export type FormTemplatePagedFilter = {
  pageNumber: number;
  pageSize: number;
  sortBy?: string;
} & GetAllTemplatesRequest;

export type FormTemplateConfigurationPagedFilter = {
  moduleId?: string;
} & FormTemplatePagedFilter;

export const formTemplateCache = new Cache({ defaultTtl: 5 * 1000 }); // 5s default Time To Live on cache items

export type FormAnswerResponse = {
  actionId: string;
  data: any;
};

export type AnswerRequest = {
  clientFormId: string;
  actionId: string;
  data: any;
  addedPlaceholders: FormBuilderPlaceholder[] | undefined;
  removedPlaceholders: string[] | undefined;
};

export type AnswerResponse = {
  id: string;
  addedQuestions: FormAction[];
  addedAnswers: FormAnswerResponse[];
  removedQuestions: string[];
  questionRequiredChanged: Record<string, boolean>;
  clientFormProgress: Progress;
  sectionProgress: Record<string, Progress>;
  sectionStatuses: Record<string, ClientFormSectionStatus>;
  allStepsValid: boolean;
  clientFormId: string;
  parentSectionStatus: ClientFormSectionStatus;
  parentSectionId: string;
  placeholders: PlaceholderAnswerResponse;
  data: any;
  clientFormSubtitle: string;
  updatedPreselectedFormTitles: Record<string, string>;
};

type PlaceholderAnswerResponse = { addedLookups: Record<string, ActionPlaceholderData>; removedLookups: Record<string, ActionPlaceholderData> };

export type AdHocAnswerResponse = {
  id: string;
  data: any;
  question: Record<string, any> & { type: ActionTypeNames };
  sortOrder: number;
  actionId: string;
  fieldId: string;
  placeholders: PlaceholderAnswerResponse;
  numberingLevel: NumberingLevel;

  //used for display purposes
  number?: string;
  nextNumbers?: {
    current: string;
    below: string;
  };
};

export enum NumberingLevel {
  None = 0,
  Current = 1,
  Below = 2,
}

export type AdHocFormAnswerRequest = {
  fieldId: string;
  question: Record<string, any> & { type: ActionTypeNames };
  sortOrder: number;
  numberingLevel: NumberingLevel;
} & AnswerRequest;

export type PublicAdHocFormAnswerRequest = {
  fieldId: string;
  question: Record<string, any> & { type: ActionTypeNames };
  data: any;
  sortOrder: number;
  actionId: string;
  numberingLevel: NumberingLevel;
};

export type Progress = {
  totalAnswers: number;
  totalRequired: number;
};

export type BulkFormAnswerPayload = {
  clientFormId: string;
  answers: BulkFormAnswers[];
};

export type BulkFormAnswers = {
  actionId: string;
  data: any;
};
